import React, { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronDown, Check } from "lucide-react";

const EASE = [0.22, 1, 0.36, 1];

function LangSwitcher({ languages, currentLang, onChange }) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    const onDown = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const current = languages.find((l) => l.value === currentLang) || languages[0];

  return (
    <div ref={rootRef} className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className={`flex items-center gap-[6px] h-[36px] cursor-my font-['Inter'] text-[13px] font-medium tracking-[1.4px] uppercase transition-colors duration-200 ${
          open ? "text-white" : "text-[#8f8f8f] hover:text-white"
        }`}
        aria-label="Language"
      >
        <span className="relative inline-flex h-[16px] w-[26px] justify-center overflow-hidden">
          <AnimatePresence mode="popLayout" initial={false}>
            <motion.span
              key={current.value}
              className="inline-block leading-[16px]"
              initial={{ y: 16, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              exit={{ y: -16, opacity: 0 }}
              transition={{ duration: 0.28, ease: EASE }}
            >
              {current.label}
            </motion.span>
          </AnimatePresence>
        </span>
        <motion.span
          className="inline-flex"
          animate={{ rotate: open ? 180 : 0 }}
          transition={{ duration: 0.25, ease: EASE }}
        >
          <ChevronDown size={14} strokeWidth={2} />
        </motion.span>
      </button>

      <AnimatePresence>
        {open && (
          <motion.div
            className="absolute top-[calc(100%+14px)] left-1/2 ml-[-60px] w-[120px] rounded-[16px] border border-[rgba(255,255,255,0.1)] bg-[rgba(2,2,2,0.96)] p-[8px] shadow-[0_18px_50px_rgba(0,0,0,0.45)] backdrop-blur-[14px] overflow-hidden origin-top"
            initial={{ opacity: 0, y: -10, scale: 0.92 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -8, scale: 0.95 }}
            transition={{ duration: 0.22, ease: EASE }}
          >
            {languages.map((language, index) => (
              <motion.button
                key={language.value}
                type="button"
                onClick={() => {
                  setOpen(false);
                  onChange(language);
                }}
                className={`flex w-full items-center justify-between rounded-[10px] px-[12px] py-[9px] font-['Inter'] text-[13px] font-medium tracking-[1.4px] uppercase cursor-my transition-colors duration-150 ${
                  currentLang === language.value
                    ? "bg-[rgba(238,84,20,0.15)] text-[#ee5414]"
                    : "text-[#8f8f8f] hover:bg-[rgba(255,255,255,0.06)] hover:text-white"
                }`}
                initial={{ opacity: 0, x: -12 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.05 + index * 0.035, duration: 0.25, ease: EASE }}
              >
                <span>{language.label}</span>
                {currentLang === language.value && <Check size={13} strokeWidth={2.5} />}
              </motion.button>
            ))}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

export default LangSwitcher;
